import {inject, Injectable} from '@angular/core';
import {skip, take} from 'rxjs/operators';
import {AuthInfoFactory} from '../../auth/auth-info-factory';
import {AuthenticationTokenBearerService} from '../../auth/authentication-token-bearer.service';
import {ConfigurationService} from '../../core/configuration/services/configuration.service';
import {ServiceCase} from '../../core/service-case/models/service-case';
import {ServiceCaseHolder} from '../../core/service-case/models/service-case-holder';
import {UserData} from '../../core/user/models/user-data';
import {UserService} from '../../core/user/services/user.service';
import {IWebComponentAppConfiguration} from '../../interfaces/IWebComponentAppConfiguration';
import {Util} from '../../util/util';
import {DmsService} from '../dms-search/dms.service';
import {IWebComponentApp} from './IWebComponentApp';
import {WebComponentInstance} from './web-component.instance';

@Injectable({
  providedIn: 'root'
})
/**
 * This service manages web components. Web component creation and visibility is managed by WebComponentComponent,
 * which is instantiated by Angular routing each time a web component app is opened. Web components are not destroyed
 * once they have been created. Separate web components are created for all service cases.
 */
export class WebComponentService {
  /**
   * Routing paths of web component apps which read the UI language from a 'language' attribute. Their language must
   * be refreshed immediately (not only via the throttled full reload) whenever the user's locale changes, see
   * ISPI-484765.
   */
  private static readonly LANGUAGE_ATTRIBUTE_ROUTING_PATHS: string[] = [
    'potentiallist',
    'marketconfiguration',
    'editingaudit',
    'dealerdashboard',
    'ccrreporting'
  ];

  private readonly dmsService: DmsService = inject(DmsService);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService =
    inject(AuthenticationTokenBearerService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly userService: UserService = inject(UserService);

  // map of web component apps which have already been initialized, with their routingPath as key
  private apps: { [routingPath: string]: IWebComponentApp } = {};

  private reloadTimeout: number;

  /**
   * Stores the Business-Partner-ID at the time of the last web component reload (or initial creation).
   * Used to detect stale attribute updates during outlet switches (ISPI-481520).
   */
  private lastReloadedBusinessPartnerId: string | null = null;

  constructor() {
    this.createSubscriptions();
  }

  /**
   * Returns the current Business-Partner-ID from active user state.
   */
  private getCurrentBusinessPartnerId(): string | null {
    return this.userService.getCurrentUser()?.getBusinessPartner()?.getBusinessPartnerId() ?? null;
  }

  private static deleteWebComponent(app: IWebComponentApp, serviceCaseId: string) {
    app.webComponentElements[serviceCaseId].destroy();
    delete app.webComponentElements[serviceCaseId];
  }

  /**
   * Show a web component app. Will also create the web component if it does not yet exist.
   * @param serviceCaseId The ID of the service case to show.
   * @param appConfiguration The configuration of the app that should be shown.
   * @param additionalAttributes Additional attributes passed to NavigationService.navigateTo()
   */
  showApp(serviceCaseId: string, appConfiguration: IWebComponentAppConfiguration, additionalAttributes?: object): void {
    this.hideAllApps();

    // for each service case, a separate web component of an app created
    // instances are identified by their service case ID, use 0 for apps without case bar so only one instance is
    // created
    if (appConfiguration.hideCaseBar) {
      serviceCaseId = '0';
    }

    // Wait for configuration to load to prevent a web component reload directly after creation because of a config
    // change. dmsSettingsChanged is a replay subject and also emits when configuration has already been loaded.
    this.dmsService.dmsSettingsChanged
      .pipe(take(1))
      .subscribe(() => this.createOrShowWebComponent(serviceCaseId, appConfiguration, additionalAttributes));
  }

  private createOrShowWebComponent(serviceCaseId: string,
                                   appConfiguration: IWebComponentAppConfiguration,
                                   additionalAttributes: object) {
    this.createWebComponentIfItDoesNotExist(serviceCaseId, appConfiguration, additionalAttributes);
    if (WebComponentInstance.isAppActive(appConfiguration)) {
      this.setWebComponentVisibility(serviceCaseId, appConfiguration.routingPath, true);
    } else {
      this.setWebComponentVisibility(serviceCaseId, appConfiguration.routingPath, false);
    }
  }

  /**
   * Hide all web component apps.
   */
  hideAllApps(): void {
    Object.values(this.apps).forEach(app =>
      Object.keys(app.webComponentElements).forEach(serviceCaseId => this.setWebComponentVisibility(
        serviceCaseId,
        app.config.routingPath,
        false
      ))
    );
  }

  /**
   * Delete all web components which belong to a certain service case.
   * @param serviceCaseId The external ID of the service case whose web components should be deleted.
   */
  deleteWebComponentsOfCase(serviceCaseId: string): void {
    Object.values(this.apps).forEach(app =>
      Object.keys(app.webComponentElements).forEach(caseId => {
        if (serviceCaseId === caseId) {
          WebComponentService.deleteWebComponent(app, caseId);
        }
      })
    );
  }

  /**
   * Init a web component app. This will register the app in this service, load source(s) and create the web component
   * in the DOM. The app will not be visible initially.
   * @param config The web component app config.
   * @param webComponentUrls The web component's source URLs.
   * @param eventListeners An object containing event listeners which should be registered on the web component.
   * @param getAttributes A function which returns attributes which should be set on the web component.
   * @return A promise which is resolved if the source(s) finished loading or rejects if loading failed.
   */
  initApp(config: IWebComponentAppConfiguration,
          webComponentUrls: string[],
          eventListeners?: any,
          getAttributes?: any): Promise<void> {
    if (!this.apps[config.routingPath]) {
      this.apps[config.routingPath] = {
        config,
        webComponentUrls,
        eventListeners,
        getAttributes,
        webComponentElements: {}
      };
    }
    const app = this.apps[config.routingPath];
    app.appInitiating ??= this.loadWebComponentSources(app);
    return app.appInitiating;
  }

  /**
   * Load web component source(s).
   * @param app The web component app instance.
   * @return A promise which is resolved if the source(s) finished loading or rejects if loading failed.
   */
  private loadWebComponentSources(app: IWebComponentApp): Promise<void> {
    return new Promise<void>(((resolve, reject) => {
      app.targetDocument = document;
      this.attachScripts(app.webComponentUrls, app.targetDocument)
        .then(() => {
          this.loadAdditionalSources(app);
          resolve();
        })
        .catch(reject);
    }));
  }

  /**
   * Retrieve additional sources and attach them to the DOM.
   * @param app The web component instance.
   */
  private loadAdditionalSources(app: IWebComponentApp): void {
    app.sourceLoadingErrors = [];
    this.getAdditionalSourceUrls(app.config)
      .forEach(src => {
        this.attachScripts([src], app.targetDocument)
          .catch(() => {
            if (src.includes('live-diagnosis')) {
              app.sourceLoadingErrors.push('showLiveDiagnosisError');
            } else if (src.includes('tyre-service')) {
              app.sourceLoadingErrors.push('showTyreServiceError');
            } else if (src.includes('service-contracts')) {
              app.sourceLoadingErrors.push('showServiceContractsError');
            } else if (src.includes('check-control-messages')) {
              app.sourceLoadingErrors.push('showCheckControlMessagesError');
            } else if (src.includes('remote-key-read')) {
              app.sourceLoadingErrors.push('showRemoteKeyReadError');
            } else if (src.includes('battery-health')) {
              app.sourceLoadingErrors.push('showBatteryHealthError');
            } else if (src.includes('fault-codes')) {
              app.sourceLoadingErrors.push('showFaultCodesError');
            } else if (src.includes('mileage')) {
              app.sourceLoadingErrors.push('showEditMileageError');
            } else if (src.includes('ds-ui')) {
              app.sourceLoadingErrors.push('showDataStorageError');
            } else if (src.includes('digital-key')) {
              app.sourceLoadingErrors.push('showDigitalKeyError');
            } else if (src.includes('remote-software-update')) {
              app.sourceLoadingErrors.push('showRemoteSoftwareUpdateError');
            } else if (src.includes('booked-and-bookable')) {
              app.sourceLoadingErrors.push('showBookedAndBookableError');
            } else if (src.includes('rspretailcrm')) {
              app.sourceLoadingErrors.push('showCustomerDataError');
            }
          });
      });
  }

  /**
   * Returns the additional source URLs that need to be loaded.
   * @param appConfig The app configuration.
   */
  private getAdditionalSourceUrls(appConfig: IWebComponentAppConfiguration): string[] {
    const sources = ConfigurationService.usesIntranetUrls()
      ? appConfig.additionalSources?.b2dIntranet
      : appConfig.additionalSources?.b2d;
    return sources || [];
  }

  /**
   * Create the web component if it does not yet exist.
   * @param serviceCaseId The ID of the service case to show.
   * @param appConfig The app configuration.
   * @param additionalAttributes attributes from NavigationService.navigateTo() as additional attributes on the web
   *   component
   */
  private createWebComponentIfItDoesNotExist(serviceCaseId: string,
                                             appConfig: IWebComponentAppConfiguration,
                                             additionalAttributes?: object): void {
    const app = this.apps[appConfig.routingPath];
    if (!app.webComponentElements[serviceCaseId]) {
      app.webComponentElements[serviceCaseId] = new WebComponentInstance(app, additionalAttributes);
    } else if (additionalAttributes) {
      // If the web component already exists, (re)apply its navigateTo attributes. Force the write
      // even when the value is unchanged so the web component's attributeChangedCallback fires
      // again and the app re-receives one-shot navigation signals (e.g. appointment='true') on a
      // repeated navigateTo. See INC000111195634.
      Object.keys(additionalAttributes).forEach(key => {
        app.webComponentElements[serviceCaseId].setAttribute(key, additionalAttributes[key], true);
      });
    }
  }

  /**
   * Show or hide a web component.
   * @param serviceCaseId The ID of the service case to show.
   * @param routingPath The app's routing path.
   * @param visible True if the app should be shown.
   */
  private setWebComponentVisibility(serviceCaseId: string, routingPath: string, visible: boolean): void {
    const webComponentInstance = this.apps[routingPath];
    if (webComponentInstance?.webComponentElements[serviceCaseId]) {
      webComponentInstance?.webComponentElements[serviceCaseId].setVisibility(visible);
    }
  }

  /**
   * Create subscriptions. Mind that subscriptions are also active while the web component is hidden.
   */
  private createSubscriptions(): void {
    this.authenticationTokenBearerService.oidcTokenSubject.subscribe((): void => {
      const user: UserData = this.userService.getCurrentUser();
      if (user) {
        this.setAttributeOnWebComponents(
          'auth-info',
          AuthInfoFactory.makeAuthInfo(user, this.serviceCaseHolder.getActiveCase()?.getBrand())
        );
      }
      this.setAttributeOnWebComponents('headers', this.getHeaders());
    });

    this.authenticationTokenBearerService.csslTokenSubject.subscribe((): void => {
      const user: UserData = this.userService.getCurrentUser();
      if (user) {
        // ISPI-481520: A new outlet's token may arrive before its user snapshot is committed or its components reload.
        const currentBusinessPartnerId: string | null = this.getCurrentBusinessPartnerId();
        const tokenBusinessPartnerId: string | null = this.authenticationTokenBearerService.csslTokenBusinessPartnerId;
        if (currentBusinessPartnerId !== this.lastReloadedBusinessPartnerId
          || (tokenBusinessPartnerId != null && tokenBusinessPartnerId !== currentBusinessPartnerId)) {
          return;
        }
        this.setAttributeOnWebComponents(
          'auth-info',
          AuthInfoFactory.makeAuthInfo(user, this.serviceCaseHolder.getActiveCase()?.getBrand())
        );
      }
    });

    // Skip first change to avoid immediate WC reload on first load.
    this.dmsService.dmsSettingsChanged
      .pipe(skip(1))
      .subscribe((): void => this.reloadWebComponentsThrottled());

    // user$ replays the current user immediately. We're only interested in changes, so skip
    // the first value.
    this.userService.user$.pipe(skip(1)).subscribe((user: UserData): void => {
      // ISPI-484765: update the 'language' attribute right away. Do not rely solely on the throttled full reload
      // below, as it can be cancelled by a browser reload (e.g. triggered right after saving user settings) before
      // its timeout fires.
      this.updateLanguageOnWebComponents(user);
      this.reloadWebComponentsThrottled();
    });


    this.serviceCaseHolder.activeServiceCaseChanged.subscribe((): void => this.onActiveCaseChange());

    // Initialize the BP-ID snapshot so that the first csslToken event is not incorrectly blocked.
    this.lastReloadedBusinessPartnerId = this.getCurrentBusinessPartnerId();
  }

  private onActiveCaseChange(): void {
    // service case related data need to be updated on web components, as they might not
    // be available when the web components are created
    const activeServiceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    if (!activeServiceCase) {
      return;
    }

    // TODO: Is there a generic way to update (changed) attributes?
    const user: UserData = this.userService.getCurrentUser();
    this.setAttributeOnWebComponents(
      'auth-info',
      AuthInfoFactory.makeAuthInfo(user, activeServiceCase.getBrand())
    );
    this.updateServiceCaseOnServiceCaseDetails(activeServiceCase);
    this.updateServiceCaseOnLeadAndDemandOverview(activeServiceCase);
    this.updateServiceCaseOnServicePreparation(activeServiceCase);
  }

  private updateServiceCaseOnServiceCaseDetails(activeServiceCase: ServiceCase): void {
    const serviceCaseDetailsApp: IWebComponentApp = Object.values(this.apps)
      .find(app => app.config.routingPath === 'casedetails');
    Object.keys(serviceCaseDetailsApp?.webComponentElements || {})
      .filter(serviceCaseId => serviceCaseId === activeServiceCase.getExternalId())
      .forEach(serviceCaseId => serviceCaseDetailsApp.webComponentElements[serviceCaseId].setAttribute(
        'active-case-data', activeServiceCase
      ));
  }

  private updateServiceCaseOnServicePreparation(activeServiceCase: ServiceCase): void {
    const servicePrepApp: IWebComponentApp = Object.values(this.apps)
      .find(app => app.config.routingPath === 'servicepreparation');
    Object.keys(servicePrepApp?.webComponentElements || {})
      .filter(serviceCaseId => serviceCaseId === activeServiceCase.getExternalId())
      .forEach(serviceCaseId => servicePrepApp.webComponentElements[serviceCaseId].setAttribute(
        'gCID',
        activeServiceCase?.getGcdmCustomerId()
      ));
  }

  private updateServiceCaseOnLeadAndDemandOverview(activeServiceCase: ServiceCase): void {
    const ladApp: IWebComponentApp = Object.values(this.apps)
      .find(app => app.config.routingPath === 'servicedemand');
    Object.keys(ladApp?.webComponentElements || {})
      .filter(serviceCaseId => serviceCaseId === activeServiceCase.getExternalId())
      .forEach(serviceCaseId => {
        ladApp.webComponentElements[serviceCaseId].setAttribute(
          'mileage',
          activeServiceCase?.getMileageRecord()
        );
        ladApp.webComponentElements[serviceCaseId].setAttribute(
          'mileage-in-kilometers',
          activeServiceCase?.getCurrentMileageInKilometers()
        );
        ladApp.webComponentElements[serviceCaseId].setAttribute(
          'service-case-id',
          activeServiceCase?.getExternalId()
        );
      });
  }

  private setAttributeOnWebComponents(name: string, value: any): void {
    Object.values(this.apps).forEach(app =>
      Object.values(app.webComponentElements).forEach(webComponent => webComponent.setAttribute(name, value))
    );
  }

  /**
   * Refresh the 'language' attribute on web components which read the UI language from it (see
   * LANGUAGE_ATTRIBUTE_ROUTING_PATHS), so they reflect the user's current locale as soon as it changes.
   * @param user The current user, providing the up-to-date locale.
   */
  private updateLanguageOnWebComponents(user: UserData): void {
    if (!user) {
      return;
    }
    const locale: string = user.getLocale();
    Object.values(this.apps)
      .filter((app: IWebComponentApp): boolean =>
        WebComponentService.LANGUAGE_ATTRIBUTE_ROUTING_PATHS.includes(app.config.routingPath))
      .forEach((app: IWebComponentApp): void =>
        Object.values(app.webComponentElements)
          .forEach((webComponent: WebComponentInstance): void => webComponent.setAttribute('language', locale)));
  }

  /**
   * Returns the headers a web component should add to its requests.
   */
  private getHeaders(): { [key: string]: string } {
    const headers = this.authenticationTokenBearerService.oidcTokenSubject.getValue().getHeaders();
    headers['Auth-App-Id'] = 'awp';
    headers['Cavors-Session-Id'] = sessionStorage.getItem('cavors-session-id') || '';
    return headers;
  }

  private reloadWebComponentsThrottled(): void {
    this.reloadTimeout && clearTimeout(this.reloadTimeout);
    this.reloadTimeout = window.setTimeout(() => this.reloadWebComponents(), 400);
  }

  /**
   * Destroy and recreate all web components.
   */
  private reloadWebComponents(): void {
    Object.values(this.apps).forEach((app: IWebComponentApp) =>
      Object.keys(app.webComponentElements).forEach((serviceCaseId: string): void => {
        if (serviceCaseId === '0' || serviceCaseId === this.serviceCaseHolder.getActiveCase()?.getExternalId()) {
          // reload active web component or web components without case immediately
          app.webComponentElements[serviceCaseId].reload();
        } else {
          // simply delete web components in other tabs, they will be reloaded once they're shown again
          WebComponentService.deleteWebComponent(app, serviceCaseId);
        }
      })
    );
    // ISPI-481520: Update snapshot after reload so subsequent csslToken events are correctly applied
    // to the freshly created web component instances.
    this.lastReloadedBusinessPartnerId = this.getCurrentBusinessPartnerId();
  }

  /**
   * Attach script elements to the DOM, unless they already exist.
   * @param sources The source URLs of the script elements as a string array.
   * @param targetDocument The document to append the script to.
   * @return A void promise which is resolved once the scripts have loaded and fails if loading fails.
   */
  private attachScripts(sources: string[], targetDocument: Document): Promise<any[]> {
    const scriptPromises = sources.map(src => Util.attachScript(src, targetDocument));
    return Promise.all(scriptPromises);
  }
}
